import { computed, Injectable, signal } from '@angular/core';
import { Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { ApiService } from './api.service';
import { UserService } from './user.service';

/**
 * Stages du parcours d'authentification (un seul écran, switch in-place).
 */
export type AuthStage =
	| 'login'         // connexion email/mdp
	| 'inscription'   // démarrage inscription email
	| 'otp'           // modale 2 : saisie du code
	| 'password'      // modale 3 : définition du mot de passe
	| 'preferences'   // modale 4 : newsletter + persona
	| 'forgot'        // sous-parcours mot de passe oublié
	| 'reset';        // sous-parcours saisie du code de reset

export interface LoginSuccessResponse {
	accessToken: string;
	refreshToken: string;
}

@Injectable()
export class AuthService {
	loginAttempted = signal(false);
	sessionReady = signal(false);
	isLoggedIn = computed(() => this.userService.user() !== null);

	loginFailed = signal(false);
	registerFailed = signal(false);

	stage = signal<AuthStage>('login');
	authOrigin = signal<'login' | 'register'>('login');
	pendingEmail = signal<string | null>(null);
	activationToken = signal<string | null>(null);
	profileCompleted = signal<boolean>(true);
	busy = signal(false);
	errorMessage = signal<string | null>(null);

	constructor(
		private api: ApiService,
		private router: Router,
		private toastrService: ToastrService,
		private userService: UserService,
	) {}

	// --------------------------------------------------------------------------
	// Init (boot)
	// --------------------------------------------------------------------------

	async init(): Promise<void> {
		const camedFromHash = this.consumeTokensFromUrlHash();
		try {
			if (!this.api.getAccessToken()) {
				this.userService.user.set(null);
				this.userService.account.set(null);
				this.userService.accountType.set(null);
				return;
			}
			await this.userService.loadEpstopAccount();

			// Si on vient d'une webview (tokens en hash) OU si l'utilisateur
			// atterrit sur la racine / sur `/welcome/*` tout en étant déjà
			// authentifié, on le pousse directement dans le dashboard.
			// La navigation est différée via setTimeout pour être exécutée
			// APRÈS le bootstrap du router (APP_INITIALIZER s'exécute avant
			// que le router ne soit actif).
			if (this.userService.user()) {
				queueMicrotask(() => {
					void this.redirectFromWelcomeIfNeeded(camedFromHash);
				});
			}
		} catch {
			this.api.clearTokens();
			this.userService.user.set(null);
			this.userService.account.set(null);
			this.userService.accountType.set(null);
		} finally {
			this.sessionReady.set(true);
			this.loginAttempted.set(true);
		}
	}

	/**
	 * Si la console est ouverte depuis la mobile app (webview) avec
	 * `#at=<access>&rt=<refresh>` en fragment d'URL, on installe les tokens
	 * directement et on nettoie l'URL pour éviter qu'ils ne persistent dans
	 * l'historique du navigateur.
	 *
	 * Retourne `true` quand des tokens ont été consommés (utilisé pour
	 * forcer la redirection vers le dashboard après chargement du profil).
	 */
	private consumeTokensFromUrlHash(): boolean {
		if (!window.location.hash) {
			return false;
		}
		const hash = window.location.hash.startsWith('#')
			? window.location.hash.slice(1)
			: window.location.hash;
		const params = new URLSearchParams(hash);
		const at = params.get('at');
		const rt = params.get('rt');
		if (!at) {
			return false;
		}
		this.api.setTokens(at, rt ?? '');
		// Nettoie uniquement les paramètres d'auth : préserve les autres si
		// jamais l'URL embarquait des routes/flags via fragment.
		params.delete('at');
		params.delete('rt');
		const remaining = params.toString();
		const newHash = remaining ? `#${remaining}` : '';
		history.replaceState(
			null,
			'',
			window.location.pathname + window.location.search + newHash,
		);
		return true;
	}

	// --------------------------------------------------------------------------
	// Stage & state helpers
	// --------------------------------------------------------------------------

	goStage(stage: AuthStage, keepEmail = true): void {
		this.stage.set(stage);
		this.errorMessage.set(null);
		if (!keepEmail) {
			this.pendingEmail.set(null);
		}
	}

	// --------------------------------------------------------------------------
	// Pré-vérification de l'email
	// --------------------------------------------------------------------------

	async preLogin(email: string): Promise<boolean> {
		try {
			const res = await firstValueFrom(
				this.api.post<{ requiresActivation: boolean }>('/auth/pre-login', {
					email: email.trim().toLowerCase(),
				}),
			);
			return res.requiresActivation;
		} catch {
			return false;
		}
	}

	// --------------------------------------------------------------------------
	// Login (email + password)
	// --------------------------------------------------------------------------

	async login(email: string, password: string): Promise<void> {
		this.busy.set(true);
		this.loginFailed.set(false);
		this.errorMessage.set(null);
		try {
			const tokens = await firstValueFrom(
				this.api.post<LoginSuccessResponse>('/auth/login', { email, password }),
			);
			this.api.setTokens(tokens.accessToken, tokens.refreshToken);
			this.pendingEmail.set(null);
			await this.afterAuthRouting();
		} catch (e: any) {
			const code = e?.error?.error;
			if (code === 'ACTIVATION_REQUIRED' || code === 'EMAIL_NOT_VERIFIED') {
				this.authOrigin.set('login');
				this.pendingEmail.set(email.trim().toLowerCase());
				await this.resendOtp(email, { silentCooldown: true });
				this.goStage('otp');
				return;
			}
			this.loginFailed.set(true);
			const reason =
				e?.status === 403
					? 'Compte temporairement verrouillé, réessayez plus tard.'
					: 'Email ou mot de passe invalide';
			this.errorMessage.set(reason);
			this.toastrService.error(reason, 'Erreur');
		} finally {
			this.busy.set(false);
		}
	}

	// --------------------------------------------------------------------------
	// Activation (verify OTP + complete with password)
	// --------------------------------------------------------------------------

	async verifyOtp(email: string, code: string): Promise<void> {
		this.busy.set(true);
		this.errorMessage.set(null);
		try {
			const res = await firstValueFrom(
				this.api.post<{ activationToken: string }>('/auth/activate/verify', {
					email: email.trim().toLowerCase(),
					code,
				}),
			);
			this.activationToken.set(res.activationToken);
			this.goStage('password');
		} catch (e: any) {
			const msg = e?.error?.message ?? 'Code invalide ou expiré';
			this.errorMessage.set(msg);
			this.toastrService.error(msg, 'Erreur');
		} finally {
			this.busy.set(false);
		}
	}

	async completeActivation(password: string): Promise<void> {
		const token = this.activationToken();
		if (!token) {
			this.errorMessage.set("Session d'activation expirée.");
			this.goStage('inscription');
			await this.router.navigate(['/welcome', 'sign-up', 'email']);
			return;
		}
		this.busy.set(true);
		this.errorMessage.set(null);
		try {
			const tokens = await firstValueFrom(
				this.api.post<LoginSuccessResponse>('/auth/activate/complete', {
					activationToken: token,
					password,
				}),
			);
			this.api.setTokens(tokens.accessToken, tokens.refreshToken);
			this.activationToken.set(null);
			this.pendingEmail.set(null);
			await this.afterAuthRouting();
		} catch (e: any) {
			const msg = e?.error?.message ?? 'Mot de passe invalide';
			this.errorMessage.set(msg);
			this.toastrService.error(msg, 'Erreur');
		} finally {
			this.busy.set(false);
		}
	}

	async resendOtp(
		email: string,
		options?: { silentCooldown?: boolean },
	): Promise<void> {
		this.busy.set(true);
		try {
			await firstValueFrom(
				this.api.post<{ message: string }>('/auth/register/resend-code', {
					email: email.trim().toLowerCase(),
				}),
			);
			this.toastrService.success('Un code a été envoyé par email.', 'Code renvoyé');
		} catch (e: any) {
			if (e?.status === 403 && options?.silentCooldown) {
				return;
			}
			if (e?.status === 429) {
				this.toastrService.warning(
					'Patientez avant de redemander un code.',
					'Trop rapide',
				);
			} else if (e?.status !== 403) {
				this.toastrService.error("Impossible d'envoyer le code", 'Erreur');
			}
		} finally {
			this.busy.set(false);
		}
	}

	// --------------------------------------------------------------------------
	// Register
	// --------------------------------------------------------------------------

	async register(email: string): Promise<void> {
		this.busy.set(true);
		this.registerFailed.set(false);
		this.errorMessage.set(null);
		try {
			await firstValueFrom(
				this.api.post<{ message: string }>('/auth/register', {
					email: email.trim().toLowerCase(),
				}),
			);
			this.authOrigin.set('register');
			this.pendingEmail.set(email.trim().toLowerCase());
			this.goStage('otp');
			this.toastrService.success(
				'Compte créé. Saisissez le code reçu par email.',
				'Succès',
			);
		} catch (e: any) {
			if (e?.status === 409) {
				this.registerFailed.set(true);
				const msg = 'Cet email est déjà utilisé';
				this.errorMessage.set(msg);
				this.toastrService.error(msg, 'Erreur');
			} else if (e?.status === 403) {
				this.authOrigin.set('register');
				this.pendingEmail.set(email.trim().toLowerCase());
				this.goStage('otp');
			} else {
				const msg = e?.error?.message ?? 'Impossible de créer le compte';
				this.errorMessage.set(msg);
				this.toastrService.error(msg, 'Erreur');
			}
		} finally {
			this.busy.set(false);
		}
	}

	// --------------------------------------------------------------------------
	// Profil (newsletter + persona)
	// --------------------------------------------------------------------------

	async updateProfile(params: {
		accountType: 'scolaire' | 'particulier';
		newsletterOptIn: boolean;
	}): Promise<void> {
		this.busy.set(true);
		try {
			await firstValueFrom(
				this.api.patch<{ message: string }>('/auth/profile', params),
			);
			this.profileCompleted.set(true);
			await this.afterAuthRouting();
		} catch {
			this.toastrService.error("Impossible d'enregistrer les préférences", 'Erreur');
		} finally {
			this.busy.set(false);
		}
	}

	// --------------------------------------------------------------------------
	// Forgot / Reset password
	// --------------------------------------------------------------------------

	async forgotPassword(email: string): Promise<void> {
		this.busy.set(true);
		try {
			await firstValueFrom(
				this.api.post<{ message: string }>('/auth/password/forgot', {
					email: email.trim().toLowerCase(),
				}),
			);
			this.pendingEmail.set(email.trim().toLowerCase());
			this.goStage('reset');
			this.toastrService.success(
				'Si un compte existe, un code a été envoyé.',
				'Email envoyé',
			);
		} catch {
			this.toastrService.error("Impossible d'envoyer le code", 'Erreur');
		} finally {
			this.busy.set(false);
		}
	}

	async resetPassword(
		email: string,
		code: string,
		password: string,
	): Promise<void> {
		this.busy.set(true);
		this.errorMessage.set(null);
		try {
			await firstValueFrom(
				this.api.post<{ message: string }>('/auth/password/reset', {
					email: email.trim().toLowerCase(),
					code,
					password,
				}),
			);
			this.toastrService.success(
				'Mot de passe mis à jour. Connectez-vous avec votre nouveau mot de passe.',
				'Succès',
			);
			this.goStage('login');
			await this.router.navigate(['/welcome', 'sign-in']);
		} catch (e: any) {
			const msg = e?.error?.message ?? 'Code invalide ou expiré';
			this.errorMessage.set(msg);
			this.toastrService.error(msg, 'Erreur');
		} finally {
			this.busy.set(false);
		}
	}

	// --------------------------------------------------------------------------
	// Logout
	// --------------------------------------------------------------------------

	async logout(): Promise<void> {
		try {
			await firstValueFrom(this.api.post<{ message: string }>('/auth/logout', {}));
		} catch {
			// Même si l’API échoue (réseau), on nettoie le client.
		}
		this.api.clearTokens();
		this.userService.account.set(null);
		this.userService.user.set(null);
		this.userService.accountType.set(null);
		this.goStage('login', false);
		await this.router.navigate(['/welcome', 'sign-in']);
		this.toastrService.success('Vous êtes déconnecté !', 'Succès');
	}

	// --------------------------------------------------------------------------
	// Internes
	// --------------------------------------------------------------------------

	private async afterAuthRouting(): Promise<void> {
		try {
			const me = await firstValueFrom(
				this.api.get<{ accountType: 'scolaire' | 'particulier'; profileCompleted: boolean }>(
					'/auth/me',
				),
			);
			this.profileCompleted.set(me.profileCompleted);
			if (!me.profileCompleted) {
				this.goStage('preferences');
				return;
			}
			const loaded = await this.userService.loadEpstopAccount();
			if (!loaded) {
				return;
			}
			this.toastrService.success('Vous êtes connecté !', 'Succès');
			await this.userService.navigateHome();
		} catch {
			await this.userService.navigateHome();
		}
	}

	private async redirectFromWelcomeIfNeeded(
		camedFromHash = false,
	): Promise<void> {
		const url = this.router.url || window.location.pathname || '/';
		const home = this.userService.getHomeUrl();
		const isEmbeddedSignup =
			url.includes('/sign-up') &&
			/(?:^|[?&])embedded=true(?:&|$)/.test(url);
		const needsRedirect =
			camedFromHash ||
			url === '/' ||
			url.startsWith('/welcome') ||
			url === '';
		if (needsRedirect && !url.startsWith(home) && !isEmbeddedSignup) {
			await this.router.navigateByUrl(home);
		}
	}
}
